import { IDropdownItem } from '@/interfaces/storageTracking/IDropdownItem.ts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { useFilterDataStore } from '@/stores/storageTracking/FilterDataStore.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';

export const storageTrackingCountries = [
    { value: 'AD', name: 'Andorra' }, { value: 'AL', name: 'Albania' },
    { hidden: true, value: 'AM', name: 'Armenia' }, { hidden: true, value: 'AZ', name: 'Azerbaijan' },
    { value: 'BA', name: 'Bosnia and Herzegovina' }, { value: 'BY', name: 'Belarus' },
    { hidden: true, value: 'FO', name: 'Faroe Islands' }, { hidden: true, value: 'GE', name: 'Georgia' },
    { hidden: true, value: 'IL', name: 'Israel' }, { hidden: true, value: 'IS', name: 'Iceland' },
    { value: 'LI', name: 'Liechtenstein' }, { value: 'LU', name: 'Luxembourg' },
    { hidden: true, value: 'MC', name: 'Monaco' }, { value: 'MK', name: 'The former Yugoslav Republic of Macedonia' },
    { hidden: true, value: 'MT', name: 'Malta' }, { hidden: true, value: 'RU', name: 'Russia' },
    { hidden: true, value: 'SM', name: 'San Marino' }, { hidden: true, value: 'TR', name: 'Turkey' },
    { hidden: true, value: 'VA', name: 'Holy See (Vatican City)' }, { value: 'AA', name: 'All Countries' },
    { value: 'AT', name: 'Austria' }, { value: 'BE', name: 'Belgium' }, { value: 'BG', name: 'Bulgaria' },
    { value: 'CH', name: 'Switzerland' }, { value: 'CY', name: 'Cyprus' }, { value: 'CZ', name: 'Czech Republic' },
    { value: 'DE', name: 'Germany' }, { value: 'DK', name: 'Denmark' }, { value: 'EE', name: 'Estonia' },
    { value: 'ES', name: 'Spain' }, { value: 'FI', name: 'Finland' }, { value: 'FR', name: 'France' },
    { value: 'GB', name: 'Great Britain' }, { value: 'GR', name: 'Greece' }, { value: 'HR', name: 'Croatia' },
    { value: 'HU', name: 'Hungary' }, { value: 'IE', name: 'Ireland' }, { value: 'IT', name: 'Italy' },
    { value: 'LV', name: 'Latvia' }, { value: 'LT', name: 'Lithuania' }, { value: 'NI', name: 'Northern Ireland' },
    { value: 'ME', name: 'Montenegro' }, { value: 'MD', name: 'Moldova' }, { value: 'NL', name: 'Netherlands' },
    { value: 'NO', name: 'Norway' }, { value: 'PL', name: 'Poland' }, { value: 'PT', name: 'Portugal' },
    { value: 'RS', name: 'Serbia' }, { value: 'RO', name: 'Romania' }, { value: 'SE', name: 'Sweden' },
    { value: 'SI', name: 'Slovenia' }, { value: 'SK', name: 'Slovakia' }, { value: 'UA', name: 'Ukraine' },
    { value: 'XK', name: 'Kosovo' },
];
export const visibleStorageTrackingCountries = storageTrackingCountries.filter(country => !country.hidden).map(country => country.value);
export const hiddenStorageTrackingCountries = storageTrackingCountries.filter(country => country.hidden).map(country => country.value);

export function getStorageTrackingCountryName (countryCode: string): string {
    return storageTrackingCountries.find(country => country.value === countryCode)?.name ?? countryCode;
}

export const useAreaDataStore = defineStore('areaDataStore', () => {
    const filterDataStore = useFilterDataStore();

    const countries = storageTrackingCountries;

    const filteredUserCountryList = ref<Array<IIdName>>([]);

    const visibleCountries = visibleStorageTrackingCountries;

    const unfilteredCountriesIdName = computed<Array<IIdName>>(() => {
        const listOfCountries: Array<IIdName> = [];
        filterDataStore.countryList.forEach((country: IDropdownItem) => {
            const objectToAdd: IIdName = {
                name: country.name,
                id: country.value,
            };
            listOfCountries.push(objectToAdd);
        });
        return listOfCountries;
    });

    const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

    const countriesDropdown = computed<Array<IDropdownItem>>(() => [
        ...countries.filter(a => !a.hidden)
            .map((a) => ({
                value: a.value,
                name: a.name,
                image: `/flags/${a.value.toLowerCase()}.svg`,
            }))].sort((a, b) => collator.compare(a.name, b.name)));

    function getCountryCodeFromName (name: string): string {
        return countries.find(a => a.name === name)?.value ?? '';
    }

    function getAllCountriesFromArray (areaArray: Array<string>): Array<string> {
        return areaArray.map((area) => countries.find(a => a.value === area)?.name ?? area);
    }

    function updateFilteredUserCountryList (filteredUserCountries: IDropdownItem[]): void {
        filteredUserCountryList.value = filteredUserCountries
            .map((item:IDropdownItem) => ({ id: item.value, name: item.name, disabled: item.disabled || undefined }))
            .sort((a, b) => {
                const aDisabled = a.disabled ?? false;
                const bDisabled = b.disabled ?? false;
                return Number(aDisabled) - Number(bDisabled);
            });
    }

    async function initialise (): Promise<void> {
        const filteredUserCountries = await DataApi.getFilteredUserCountries();
        updateFilteredUserCountryList(filteredUserCountries);
    }

    initialise();

    return {
        countries,
        countriesDropdown,
        filteredUserCountryList,
        getCountryName: getStorageTrackingCountryName,
        getCountryCodeFromName,
        visibleCountries,
        getAllCountriesFromArray,
        unfilteredCountriesIdName,
    };
});
